'use client';

/**
 * Client-side infinite list for the "New Recipes" section on /recipes.
 *
 * Initial page is server-rendered. Subsequent pages are fetched via the
 * `loadMoreNewRecipesAction` server action and appended in place — the URL
 * stays clean (filters live in search params, cursor is transient state).
 *
 * Accessibility:
 * - List wrapper uses aria-live="polite" so screen readers announce appended
 *   items (the status message below does the heavy lifting; the live region on
 *   the list itself is intentionally polite + atomic=false).
 * - Button carries aria-busy while fetching.
 * - Focus stays on the button after append — we don't teleport the user
 *   into the newly-appended items.
 */

import { useEffect, useRef, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { RecipeCard } from './recipe-card';
import { Button } from '@/components/ui/button';
import { Reveal } from '@/components/ui/reveal';
import { loadMoreNewRecipesAction, loadMoreUnifiedRecipesAction } from '@/lib/actions/recipe-feed';
import type { RecipeFeedItem } from '@/lib/data/discovery';
import styles from './new-recipes-stream.module.css';

/** Start loading the next page this far before the list's end scrolls into view. */
 
const AUTO_LOAD_MARGIN = '0px 0px 600px 0px';

interface NewRecipesStreamProps {
  initialRecipes: RecipeFeedItem[];
  initialCursor: string | null;
  /** Flat search params (already collapsed from string | string[]) — passed
   *  back to the server action so filters are re-parsed server-side. */
  searchParamsFlat: Record<string, string | undefined>;
  /** IDs already rendered in earlier sections (e.g. Trending) — we dedupe so
   *  the same recipe doesn't appear twice on screen. */
  excludeIds: string[];
  /** Which list to page: the home "New" feed, or the unified sort/filter
   *  browse ("See all", #1043). Defaults to `new`. */
  feed?: 'new' | 'unified';
  /** Accessible name for the list; defaults to the "New recipes" label. */
  ariaLabel?: string;
}

export function NewRecipesStream({
  initialRecipes,
  initialCursor,
  searchParamsFlat,
  excludeIds,
  feed = 'new',
  ariaLabel,
}: NewRecipesStreamProps) {
  const t = useTranslations('recipes.newRecipesStream');
  // Unnamespaced client-side translator for RecipeCard — see the comment on
  // RecipeCardProps.t. RecipeCard used to call next-intl/server's
  // getTranslations() internally, which is a server-only API; this component
  // renders RecipeCard client-side (the "load more" pagination flow), so that
  // call threw on hydration ("getTranslations is not supported in Client
  // Components"), surfacing as an unrecoverable React error #419 specifically
  // on /recipes. useTranslations() is next-intl's client-safe equivalent.
  const tCard = useTranslations();
  const [recipes, setRecipes] = useState<RecipeFeedItem[]>(initialRecipes);
  const [cursor, setCursor] = useState<string | null>(initialCursor);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedCount, setLastLoadedCount] = useState<number | null>(null);
  const [isPending, startTransition] = useTransition();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const excludeSet = new Set(excludeIds);
  const visible = recipes.filter((r) => !excludeSet.has(r.id));

  const hasMore = cursor !== null;

  // `auto` = the scroll sentinel fired. Only an explicit click moves focus:
  // focusing the button after an auto-load would yank the page to it.
  const handleLoadMore = (source: 'button' | 'auto') => {
    if (!cursor || isPending) return;
    setError(null);
    const pendingCursor = cursor;
    startTransition(async () => {
      try {
        const loadMore =
          feed === 'unified' ? loadMoreUnifiedRecipesAction : loadMoreNewRecipesAction;
        const page = await loadMore(pendingCursor, searchParamsFlat);
        setRecipes((prev) => {
          // Dedupe against what we already have (defensive — keyset pagination
          // shouldn't produce dupes, but a concurrent publish between pages
          // could in theory shift things).
          const seen = new Set(prev.map((r) => r.id));
          const fresh = page.recipes.filter((r) => !seen.has(r.id));
          return [...prev, ...fresh];
        });
        setCursor(page.nextCursor);
        setLastLoadedCount(page.recipes.length);
        // Keep focus on the button (or its successor message) for keyboard users.
        if (source === 'button') buttonRef.current?.focus();
      } catch {
        setError(t('loadMoreError'));
      }
    });
  };

  // Load the next page as the reader scrolls near the end, so the feed keeps
  // going without a tap (#1042). Bounded: it stops when the cursor runs out,
  // and it stops after a failed load — the button stays as the manual retry,
  // so a broken action can never turn into a request loop. Re-armed after each
  // load, which also fills a tall screen that still shows the sentinel.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef(handleLoadMore);
  useEffect(() => {
    loadMoreRef.current = handleLoadMore;
  });
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || isPending || error !== null) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          loadMoreRef.current('auto');
        }
      },
      { rootMargin: AUTO_LOAD_MARGIN },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isPending, error]);

  // Empty-but-more-to-come still renders the Load more button: returning null
  // here whenever the first page was all duplicates hid the only way to reach
  // the rest of the feed (#1042).
  if (visible.length === 0 && !hasMore) return null;

  return (
    <>
      <ul
        className={styles.grid}
        aria-label={ariaLabel ?? t('aria')}
        aria-live="polite"
        aria-relevant="additions"
      >
        {visible.map((recipe, i) => (
          <li key={recipe.id}>
            <Reveal index={i}>
            <RecipeCard
              t={tCard}
              recipeId={recipe.id}
              slug={recipe.slug}
              title={recipe.title}
              description={recipe.description}
              imageUrl={recipe.imageUrl}
              prepTimeMinutes={recipe.prepTimeMinutes}
              cookTimeMinutes={recipe.cookTimeMinutes}
              servings={recipe.servings}
              variantType={recipe.variantType}
              variantIsDefault={recipe.variantIsDefault}
              isVegan={recipe.isVegan}
              isVegetarian={recipe.isVegetarian}
              isGlutenFree={recipe.isGlutenFree}
              isDairyFree={recipe.isDairyFree}
              caloriesPerServing={recipe.caloriesPerServing}
            />
            </Reveal>
          </li>
        ))}
      </ul>

      {/* Polite status region — announces load results to screen readers */}
      <div className={styles.srOnly} role="status" aria-live="polite">
        {isPending
          ? t('loadingAnnouncement')
          : lastLoadedCount !== null
            ? t('loadedAnnouncement', { count: lastLoadedCount })
            : ''}
      </div>

      <div className={styles.footer} ref={sentinelRef}>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        {hasMore ? (
          <Button
            ref={buttonRef}
            type="button"
            onClick={() => handleLoadMore('button')}
            loading={isPending}
          >
            {isPending ? t('loading') : t('loadMore')}
          </Button>
        ) : (
          <p className={styles.endOfList} aria-hidden={visible.length === 0}>
            {t('endOfList')}
          </p>
        )}
      </div>
    </>
  );
}
